<script>
import pick from 'lodash/pick'
import keys from 'lodash/keys'
import debounce from 'lodash/debounce'
import { authComputed } from '@state/helpers'
import {
  notificationComputed,
  notificationMethods,
} from '@state/helpers/notification'
import {
  PLACE,
  PLACE_DETAIL,
  NOTIFICATION_STATUS,
  NOTIFICATION_STATUS_NAME,
} from '@utils/constant'
import cloneDeep from 'lodash/cloneDeep'
import basicInfoItems, { basicInfoFields, basicOptions } from './basicInfoItems'
import UploadImage from './upload-image.vue'

export default {
  components: {
    UploadImage,
  },
  props: {
    variant: {
      validator: function(value) {
        return !!~['add', 'edit'].indexOf(value)
      },
      default: 'add',
    },
    errors: {
      type: Object,
      default: () => {},
    },
    triggerMask: {
      type: Boolean,
      require: false,
    },
  },
  data() {
    return {
      items: basicInfoItems,
      fields: cloneDeep(basicInfoFields),
      options: basicOptions,
      place: `${PLACE.APP}`,
      resetPlaceholder: false,
      editable: false, // edit mode
    }
  },
  computed: {
    ...authComputed,
    ...notificationComputed,
    add() {
      return this.variant === 'add'
    },
    edit() {
      return this.variant === 'edit'
    },
    quoteOptionComputed() {
      const op = { ...this.options }
      const quotes = this.getQuotes?.map((quote) => {
        return { label: `[${quote.id}]-${quote.title}`, value: `${quote.id}` }
      })
      return { ...op, quote: quotes }
    },
    hiddenEditButton() {
      return (
        this.add ||
        (!this.add &&
          ![
            NOTIFICATION_STATUS_NAME.WAITING_FOR_DELIVERY,
            NOTIFICATION_STATUS_NAME.DELIVERY,
          ].includes(this.getNotificationDetail?.status))
      )
    },
    itemsComputed() {
      return this.items.filter((item) => {
        return (
          this.add ||
          (this.edit && (this.editable ? !item.hiddenEdit : !item.hiddenView))
        )
      })
    },
  },
  watch: {
    fields: debounce(function() {
      this.fields.order = parseInt(this.fields.order, 10)
      this.fields.id = parseInt(this.fields.id, 10)
      this.$emit('change:data', this.fields)
    }, 200),
    getNotificationDetail() {
      if (this.getNotificationDetail) {
        this.initBasicInfoData()
      }
    },
  },
  methods: {
    ...notificationMethods,
    initBasicInfoData() {
      const detail = { ...this.getNotificationDetail }
      if (this.add) {
        detail.quote = `${detail.id}`
      } else {
        detail.id = `${detail.id}`
      }
      detail.place = `${detail.place}`
      detail.order = `${detail.order}`
      detail.place_text = PLACE_DETAIL[detail.place]
      detail.status = NOTIFICATION_STATUS[detail.status]
      this.fields = pick(detail, [...keys(basicInfoFields), 'start', 'end'])
      this.items.map((item) => {
        this.fields[item.lower] = this.fields.start ? this.fields.start : '-'
        this.fields[item.upper] = this.fields.end ? this.fields.end : '-'
      })
    },
    requiredError(rules) {
      return rules?.required
    },
    getNotificationDetailById() {
      const quote = this.fields.quote || this.quoteOptionComputed.quote[0].value
      this.fetchingNotificationById({ id: parseInt(quote, 10) })
    },
    resetFile() {
      this.resetPlaceholder = true
      this.fields.image_binary = ''
      this.fields.image_url = ''
    },
    handleChangeBasicInfo(field, value) {
      if (field === 'place') {
        this.$emit('fetching:quote', parseInt(value, 10))
      }
      const fields = { ...this.fields }
      fields[field] = value
      this.fields = fields
    },
    handleUploadImage(imageBinary) {
      const a = { ...this.fields }
      a.image_binary = imageBinary
      this.fields = { ...a }
      this.fields.image_binary = imageBinary
    },
    toggleEditable() {
      this.editable = !this.editable
      this.initBasicInfoData()
      this.$emit('enable:edit', this.editable)
    },
    resetForm() {
      this.fields = { ...basicInfoFields }
    },
  },
}
</script>

<template>
  <div id="notification-basic-info">
    <BaseCardDetail
      title="基本情報"
      form-collapsed
      :errors="errors"
      :trigger-mask="!editable && triggerMask"
      :has-footer="!add && editable"
      @edit="toggleEditable"
      @cancel="toggleEditable"
      @submit="
        () => {
          toggleEditable()
          $emit('edit:notification')
        }
      "
    >
      <template v-if="hiddenEditButton" v-slot:header-action>
        <div />
      </template>
      <CRow
        v-for="item in itemsComputed"
        :key="item.key"
        class="d-flex align-items-baseline flex-wrap"
      >
        <CCol sm="3">
          <div v-if="item.variant.includes(variant)">
            <div
              v-if="(item.key === 'order') & add"
              v-show="fields.place === place"
            >
              {{ $t(`fields.${item.label}`) }}
            </div>
            <div
              v-else-if="item.key === 'order' && !add"
              v-show="fields.place === place && fields.agent_id === 0"
            >
              {{ $t(`fields.${item.label}`) }}
            </div>
            <div v-else>
              {{ $t(`fields.${item.label}`) }}
              <span v-if="requiredError(item.rules)" class="text-danger"
                >*</span
              >
            </div>
          </div>
        </CCol>
        <CCol
          sm="9"
          :class="{
            'd-flex align-items-baseline flex-wrap': ['quote'].includes(
              item.key
            ),
          }"
        >
          <div v-if="item.as === 'period' && item.variant.includes(variant)">
            {{ fields[item.lower] }} ～ {{ fields[item.upper] }}
          </div>
          <BaseRadioGroup
            v-if="item.as === 'radioGroup' && item.variant.includes(variant)"
            :options="item.options"
            inline
            custom
            :checked.sync="fields[item.key]"
            @update:checked="(v) => handleChangeBasicInfo(item.key, v)"
          />
          <BaseInput
            v-if="item.as === 'input' && item.variant.includes(variant)"
            :value.sync="fields[item.key]"
            :placeholder="add || editable ? item.placeholder : ''"
            :vee-name="item.name"
            :rules="item.rules"
            :plaintext="item.plaintext || (!add && !editable)"
            @update:value="(v) => handleChangeBasicInfo(item.key, v)"
          />
          <BaseTextarea
            v-if="item.as === 'area' && item.variant.includes(variant)"
            :value.sync="fields[item.key]"
            :placeholder="item.placeholder"
            :rules="item.rules"
            :vee-name="item.name"
            :plaintext="!add && !editable"
            @update:value="(v) => handleChangeBasicInfo(item.key, v)"
          />
          <div>
            <BaseInput
              v-if="item.as === 'inputNumber' && add"
              v-show="fields.place === place"
              :value.sync="fields[item.key]"
              type="number"
              :rules="item.rules"
              :plaintext="!add && !editable"
              @update:value="(v) => handleChangeBasicInfo(item.key, v)"
            />
            <BaseInput
              v-if="item.as === 'inputNumber' && !add"
              v-show="fields.place === place && fields.agent_id === 0"
              :value.sync="fields[item.key]"
              type="number"
              :rules="item.rules"
              :plaintext="!add && !editable"
              @update:value="(v) => handleChangeBasicInfo(item.key, v)"
            />
          </div>

          <BaseSelect
            v-if="item.as === 'select' && item.variant.includes(variant)"
            :value.sync="fields[item.key]"
            :class="{
              'mr-3 flex-1': ['quote'].includes(item.key),
            }"
            :options="quoteOptionComputed[item.key]"
            :disabled="!add && !editable"
            @update:value="(v) => handleChangeBasicInfo(item.key, v)"
          />
          <UploadImage
            v-if="item.as === 'inputFile' && item.variant.includes(variant)"
            :name="item.name"
            :disabled="!add && !editable"
            :reset-placeholder="resetPlaceholder"
            :show-reset-button="
              item.key === 'image_binary' &&
                (!!fields.image_binary || !!fields.image_url)
            "
            @upload:reset="resetFile"
            @upload:change="handleUploadImage"
          />
          <CButton
            v-if="item.key === 'quote' && item.variant.includes(variant)"
            color="dark"
            variant="outline"
            class="py-0"
            @click="getNotificationDetailById"
          >
            適用</CButton
          >
        </CCol>
      </CRow>
    </BaseCardDetail>
  </div>
</template>

<style lang="scss" scoped>
#notification-basic-info {
  ::v-deep .custom-file-label {
    padding-right: 75px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
